/**
 * Playlist Cache Service
 *
 * Infrastructure implementation of PlaylistCacheGateway for persisting
 * playlist data to localStorage with TTL validation.
 *
 * Follows Clean Architecture:
 * - Implements gateway interface owned by use-cases
 * - Keeps persistence details at the boundary
 * - Uses simple data structures for cache entries
 *
 * Key behaviors:
 * - Persists playlist to localStorage with TTL (default: 5 minutes)
 * - Validates cached data structure before returning
 * - Distinguishes between 'fresh', 'stale', and 'none' cache states
 * - Returns metadata (source, age, isStale) for logging and decision making
 * - Handles localStorage errors gracefully (quota exceeded, unavailable)
 */

import { Playlist } from '@/types/MusicPlaylist';
import {
  CacheEntry,
  CacheResult,
  CacheStatus,
  CacheStatusInfo,
  CacheStoreOptions,
  PlaylistCacheGateway,
} from '@/types/PlaylistCache';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';

/** localStorage key for playlist cache */
const CACHE_KEY = 'r2-playlist-cache';

/** Current cache schema version for migration support */
const CACHE_VERSION = 1;

/** Default TTL: 5 minutes in milliseconds */
const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * Type guard to validate cached Playlist structure
 * Ensures the data has the expected shape before use
 */
function isValidPlaylist(data: unknown): data is Playlist {
  if (!data || typeof data !== 'object') return false;

  const playlist = data as Record<string, unknown>;

  // Check required fields exist and have correct types
  if (typeof playlist.id !== 'string') return false;
  if (typeof playlist.name !== 'string') return false;
  if (!Array.isArray(playlist.tracks)) return false;

  // Validate at least one track has required fields (sample validation)
  if (playlist.tracks.length > 0) {
    const firstTrack = playlist.tracks[0] as Record<string, unknown>;
    if (typeof firstTrack.id !== 'string') return false;
    if (typeof firstTrack.title !== 'string') return false;
    if (typeof firstTrack.artist !== 'string') return false;
    if (typeof firstTrack.audioUrl !== 'string') return false;
  }

  return true;
}

/**
 * Type guard to validate CacheEntry structure
 */
function isValidCacheEntry(data: unknown): data is CacheEntry<Playlist> {
  if (!data || typeof data !== 'object') return false;

  const entry = data as Record<string, unknown>;

  // Check required cache metadata fields
  if (typeof entry.storedAt !== 'string') return false;
  if (typeof entry.updatedAt !== 'string') return false;
  if (typeof entry.ttlMs !== 'number') return false;
  if (typeof entry.version !== 'number') return false;

  // Validate the nested playlist data
  return isValidPlaylist(entry.data);
}

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Calculate cache status based on age and TTL
 */
function calculateStatus(age: number, ttlMs: number): CacheStatus {
  if (age <= ttlMs) {
    return 'fresh';
  }
  return 'stale';
}

/**
 * PlaylistCacheService
 *
 * Concrete implementation of PlaylistCacheGateway using localStorage.
 * Provides TTL-based caching with validation and metadata tracking.
 */
export class PlaylistCacheService implements PlaylistCacheGateway<Playlist> {
  private readonly cacheKey: string;
  private readonly defaultTtlMs: number;

  constructor(options?: { cacheKey?: string; defaultTtlMs?: number }) {
    this.cacheKey = options?.cacheKey ?? CACHE_KEY;
    this.defaultTtlMs = options?.defaultTtlMs ?? DEFAULT_TTL_MS;
  }

  /**
   * Retrieve cached playlist with metadata
   *
   * Returns cached data regardless of staleness - caller decides usage.
   * Validates the cached structure before returning.
   */
  get(): CacheResult<Playlist> {
    const startTime = performance.now();

    // Handle localStorage unavailable
    if (!isLocalStorageAvailable()) {
      this.logCacheOperation('get', 'none', 0, false, 'localStorage unavailable');
      return this.createEmptyResult();
    }

    try {
      const raw = localStorage.getItem(this.cacheKey);

      // No cached data
      if (!raw) {
        this.logCacheOperation('get', 'none', 0, false);
        return this.createEmptyResult();
      }

      // Parse and validate
      const parsed = JSON.parse(raw);

      if (!isValidCacheEntry(parsed)) {
        // Invalid structure - clear it and return empty
        this.clear();
        this.logCacheOperation('get', 'none', 0, false, 'invalid cache structure');
        return this.createEmptyResult();
      }

      // Check version for migration
      if (parsed.version !== CACHE_VERSION) {
        // Future: handle migration here
        this.clear();
        this.logCacheOperation('get', 'none', 0, false, `version mismatch: ${parsed.version}`);
        return this.createEmptyResult();
      }

      // Calculate age and status
      const storedTime = new Date(parsed.storedAt).getTime();
      const age = Date.now() - storedTime;
      const status = calculateStatus(age, parsed.ttlMs);
      const isStale = status === 'stale';
      const durationMs = Math.round(performance.now() - startTime);

      this.logCacheOperation('get', status, durationMs, true, undefined, age, parsed.data.tracks.length);

      return {
        data: parsed.data,
        status,
        source: isStale ? 'fallback' : 'cache',
        age,
        isStale,
        etag: parsed.etag,
      };
    } catch (error) {
      // JSON parse error or other issue - clear invalid data
      this.clear();
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.logCacheOperation('get', 'none', 0, false, `parse error: ${errorMessage}`);
      return this.createEmptyResult();
    }
  }

  /**
   * Store playlist in cache
   *
   * @param data - Playlist to cache
   * @param options - Optional TTL and ETag
   * @returns true if stored successfully, false on error
   */
  set(data: Playlist, options?: CacheStoreOptions): boolean {
    const startTime = performance.now();
    const ttlMs = options?.ttlMs ?? this.defaultTtlMs;

    // Handle localStorage unavailable
    if (!isLocalStorageAvailable()) {
      this.logCacheOperation('set', 'none', 0, false, 'localStorage unavailable');
      return false;
    }

    // Validate data before storing
    if (!isValidPlaylist(data)) {
      this.logCacheOperation('set', 'none', 0, false, 'invalid playlist data');
      return false;
    }

    const now = new Date().toISOString();
    const entry: CacheEntry<Playlist> = {
      data,
      storedAt: now,
      updatedAt: now,
      ttlMs,
      version: CACHE_VERSION,
      ...(options?.etag && { etag: options.etag }),
    };

    try {
      const serialized = JSON.stringify(entry);
      localStorage.setItem(this.cacheKey, serialized);

      const durationMs = Math.round(performance.now() - startTime);
      this.logCacheOperation('set', 'fresh', durationMs, true, undefined, 0, data.tracks.length);
      return true;
    } catch (error) {
      // Handle quota exceeded or other storage errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isQuotaError =
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' || error.code === 22);

      this.logCacheOperation(
        'set',
        'none',
        0,
        false,
        isQuotaError ? 'quota exceeded' : `storage error: ${errorMessage}`
      );
      return false;
    }
  }

  /**
   * Clear the cache
   */
  clear(): void {
    if (!isLocalStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(this.cacheKey);
      this.logCacheOperation('clear', 'none', 0, true);
    } catch {
      // Ignore errors on clear
    }
  }

  /**
   * Get cache status without retrieving full data
   *
   * Useful for checking cache state before deciding to fetch
   */
  getStatus(): CacheStatusInfo {
    if (!isLocalStorageAvailable()) {
      return {
        exists: false,
        status: 'none',
        age: 0,
        isStale: false,
        updatedAt: null,
      };
    }

    try {
      const raw = localStorage.getItem(this.cacheKey);

      if (!raw) {
        return {
          exists: false,
          status: 'none',
          age: 0,
          isStale: false,
          updatedAt: null,
        };
      }

      const parsed = JSON.parse(raw);

      if (!isValidCacheEntry(parsed)) {
        return {
          exists: false,
          status: 'none',
          age: 0,
          isStale: false,
          updatedAt: null,
        };
      }

      const storedTime = new Date(parsed.storedAt).getTime();
      const age = Date.now() - storedTime;
      const status = calculateStatus(age, parsed.ttlMs);

      return {
        exists: true,
        status,
        age,
        isStale: status === 'stale',
        updatedAt: parsed.updatedAt,
        etag: parsed.etag,
      };
    } catch {
      return {
        exists: false,
        status: 'none',
        age: 0,
        isStale: false,
        updatedAt: null,
      };
    }
  }

  /**
   * Create empty cache result
   */
  private createEmptyResult(): CacheResult<Playlist> {
    return {
      data: null,
      status: 'none',
      source: 'cache',
      age: 0,
      isStale: false,
    };
  }

  /**
   * Log cache operation using DataFetchLogger
   */
  private logCacheOperation(
    operation: 'get' | 'set' | 'clear',
    status: CacheStatus,
    durationMs: number,
    success: boolean,
    errorMessage?: string,
    cacheAge?: number,
    itemCount?: number
  ): void {
    DataFetchLogger.logFetch({
      operation: `playlist-cache:${operation}`,
      source: 'cache',
      status: success ? 'success' : 'failure',
      durationMs,
      cacheHit: operation === 'get' && success && status !== 'none',
      cacheAge: cacheAge,
      itemCount,
      errorMessage,
    });
  }
}

/**
 * Singleton instance factory
 */
let playlistCacheServiceInstance: PlaylistCacheService | null = null;

export function getPlaylistCacheService(): PlaylistCacheService {
  if (!playlistCacheServiceInstance) {
    playlistCacheServiceInstance = new PlaylistCacheService();
  }
  return playlistCacheServiceInstance;
}

/**
 * Reset singleton (for testing)
 */
export function resetPlaylistCacheService(): void {
  playlistCacheServiceInstance = null;
}
